import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useAssetOriginalUrl from "@actions/assets/useOriginalAssetUrl";
import { Asset } from "@thenounproject/lingo-core";
import PlayButtonOverlay from "@components/kits/PlayButtonOverlay";

const StyledVideo = styled.video<{ hideControls?: boolean; hugging?: boolean }>`
  object-fit: contain;
  height: 100%;
  width: 100%;

  ${({ hideControls }) =>
    hideControls &&
    `
    &::-webkit-media-controls {
      display: none !important;
    }
  `}
`;

const ClickableWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  pointer-events: auto;
`;

const VideoContainer = styled.div<{ aspectRatio?: number }>`
  position: relative;
  width: 100%;
  height: 100%;
`;

type Props = {
  asset: Asset;
  poster?: string;
  autoplay?: boolean;
};

const VideoThumbnail: React.FC<Props> = ({ asset, poster, autoplay }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: url } = useAssetOriginalUrl(asset);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsInView(entry.isIntersecting);

          if (entry.isIntersecting && autoplay && videoRef.current) {
            void videoRef.current.play().catch(() => {
              videoRef.current!.muted = true;
              void videoRef.current!.play();
            });
            setIsPlaying(true);
          } else if (!entry.isIntersecting && videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        });
      },
      {
        threshold: 0.5, // Play when 50% visible
        rootMargin: "50px",
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [autoplay, asset.id, url]);

  const handleCanPlay = async () => {
    // Only autoplay if in view and autoplay is enabled
    if (autoplay && isInView && videoRef.current) {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
      } catch {
        // Autoplay blocked, user needs to interact
      }
    }
  };

  const handlePlayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      await videoRef.current.play();
      setIsPlaying(true);
    }
  };

  if (!url) return null;

  return (
    <VideoContainer ref={containerRef}>
      <StyledVideo
        ref={videoRef}
        src={url}
        loop
        muted
        playsInline
        preload="metadata"
        controls={isPlaying && !autoplay}
        hideControls={autoplay}
        poster={poster}
        onCanPlay={handleCanPlay}
        onDragStart={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      {!isPlaying && (
        <ClickableWrapper onClick={handlePlayClick}>
          <PlayButtonOverlay iconSize={60} />
        </ClickableWrapper>
      )}
    </VideoContainer>
  );
};

export default VideoThumbnail;
